import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardCheck,
  Flag,
  Clock,
  CheckCircle,
  AlertTriangle,
  Pencil,
  Eye,
  Filter,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from "lucide-react";

const CurrencyDisplay = ({ amount }) => (
  <span>{`\u20B9${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}</span>
);

const TYPE_BADGES = {
  revenue: { label: "Revenue", className: "bg-green-100 text-green-700" },
  expense: { label: "Expense", className: "bg-orange-100 text-orange-700" },
  handover: { label: "Handover", className: "bg-blue-100 text-blue-700" },
};

const TAB_CONFIG = [
  { key: "pending", label: "Pending Review", icon: Clock, color: "text-amber-600" },
  { key: "reviewed", label: "Reviewed", icon: CheckCircle, color: "text-green-600" },
  { key: "flagged", label: "Flagged", icon: AlertTriangle, color: "text-red-600" },
];

const ReviewAuditPage = ({ user, restaurants }) => {
  const [activeTab, setActiveTab] = useState("pending");
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: { pending: 0, reviewed: 0, flagged: 0 } });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filterType, setFilterType] = useState("all");
  const [filterRestaurant, setFilterRestaurant] = useState("all");
  const [startDate, setStartDate] = useState("2026-09-01");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  // Flag dialog
  const [flagDialog, setFlagDialog] = useState(false);
  const [flagEntry, setFlagEntry] = useState(null);
  const [flagNotes, setFlagNotes] = useState("");

  // Detail/Edit dialog
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({});

  // Category names cache for revenue breakdown
  const [categoryNames, setCategoryNames] = useState({});

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ review_status: activeTab, page: String(page), page_size: String(PAGE_SIZE) });
      if (filterType !== "all") params.set("entry_type", filterType);
      if (filterRestaurant !== "all") params.set("restaurant_id", filterRestaurant);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);

      const [entriesRes, statsRes] = await Promise.all([
        axiosInstance.get(`/review/entries?${params}`),
        axiosInstance.get("/review/stats"),
      ]);
      const data = entriesRes.data;
      setEntries(data.entries || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total || 0);
      setStats(statsRes.data);
      setSelected(new Set());
    } catch (e) {
      toast.error("Failed to load review entries");
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterType, filterRestaurant, startDate, endDate, page]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [activeTab, filterType, filterRestaurant, startDate, endDate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axiosInstance.get("/revenue-categories");
        const map = {};
        res.data.forEach(c => { map[c.id] = c.name; });
        setCategoryNames(map);
      } catch {}
    };
    fetchCategories();
  }, []);

  const handleRollback = async (entryType, entryId) => {
    try {
      await axiosInstance.put(`/review/entries/${entryType}/${entryId}?action=pending`);
      toast.success("Entry rolled back to pending");
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rollback");
    }
  };

  const handleReview = async (entryType, entryId, action, notes = "") => {
    try {
      await axiosInstance.put(`/review/entries/${entryType}/${entryId}?action=${action}&notes=${encodeURIComponent(notes)}`);
      toast.success(`Entry marked as ${action}`);
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const handleBulkReview = async () => {
    const items = Array.from(selected).map(key => {
      const [entry_type, entry_id] = key.split("::");
      return { entry_type, entry_id };
    });
    try {
      const res = await axiosInstance.put("/review/bulk", items, { params: { action: "reviewed" } });
      toast.success(res.data.message);
      fetchEntries();
    } catch (e) {
      toast.error("Bulk review failed");
    }
  };

  const handleFlag = async () => {
    if (!flagEntry) return;
    await handleReview(flagEntry.entry_type, flagEntry.entry_id, "flagged", flagNotes);
    setFlagDialog(false);
    setFlagEntry(null);
    setFlagNotes("");
  };

  const handleEditSave = async () => {
    if (!detailEntry) return;
    try {
      await axiosInstance.put(
        `/review/entries/${detailEntry.entry_type}/${detailEntry.entry_id}/edit`,
        editFields
      );
      toast.success("Entry updated and marked as reviewed");
      setDetailDialog(false);
      setEditMode(false);
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleSelect = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === entries.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(entries.map(e => `${e.entry_type}::${e.entry_id}`)));
    }
  };

  const filteredEntries = entries;

  return (
    <div className="space-y-4" data-testid="review-audit-page">
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        {TAB_CONFIG.map(tab => {
          const count = stats.total?.[tab.key] || 0;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                isActive ? "border-gray-900 bg-gray-50 shadow-sm" : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`review-tab-${tab.key}`}
            >
              <tab.icon className={`w-5 h-5 ${tab.color}`} />
              <div className="text-left">
                <div className="text-xs text-gray-500">{tab.label}</div>
                <div className="text-lg font-bold">{count}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters & Bulk Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36 h-8 text-sm" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
              <SelectItem value="handover">Handover</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterRestaurant} onValueChange={setFilterRestaurant}>
            <SelectTrigger className="w-48 h-8 text-sm" data-testid="filter-restaurant">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Restaurants</SelectItem>
              {restaurants?.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {activeTab === "pending" && selected.size > 0 && (
          <Button size="sm" className="gap-2" onClick={handleBulkReview} data-testid="bulk-review-btn">
            <ClipboardCheck className="w-4 h-4" />
            Mark {selected.size} as Reviewed
          </Button>
        )}
      </div>

      {/* Date Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Calendar className="w-4 h-4 text-gray-400" />
        <div className="flex items-center gap-2">
          <Input
            type="month"
            onChange={(e) => {
              if (e.target.value) {
                const [y, m] = e.target.value.split("-");
                const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
                setStartDate(`${y}-${m}-01`);
                setEndDate(`${y}-${m}-${String(lastDay).padStart(2, '0')}`);
              }
            }}
            className="w-40 h-8 text-sm"
            data-testid="review-month-picker"
          />
          <span className="text-xs text-gray-400">or</span>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-500 whitespace-nowrap">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-36 h-8 text-sm"
            data-testid="review-start-date"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-500 whitespace-nowrap">To</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-36 h-8 text-sm"
            data-testid="review-end-date"
          />
        </div>
        {(startDate !== "2026-09-01" || endDate) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setStartDate("2026-09-01"); setEndDate(""); }}>
            Reset
          </Button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{totalCount} entries</span>
      </div>

      {/* Entries Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              No {activeTab} entries found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {activeTab === "pending" && (
                      <th className="p-3 text-left w-10">
                        <Checkbox
                          checked={selected.size === filteredEntries.length && filteredEntries.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Restaurant</th>
                    <th className="p-3 text-right">Amount</th>
                    <th className="p-3 text-left">Submitted By</th>
                    <th className="p-3 text-left">Description</th>
                    {activeTab !== "pending" && <th className="p-3 text-left">Reviewed By</th>}
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, i) => {
                    const key = `${entry.entry_type}::${entry.entry_id}`;
                    const typeBadge = TYPE_BADGES[entry.entry_type] || {};
                    return (
                      <tr key={key} className={`border-b hover:bg-gray-50 ${i % 2 ? "bg-white" : "bg-gray-50/30"}`} data-testid={`review-row-${entry.entry_id}`}>
                        {activeTab === "pending" && (
                          <td className="p-3">
                            <Checkbox
                              checked={selected.has(key)}
                              onCheckedChange={() => toggleSelect(key)}
                            />
                          </td>
                        )}
                        <td className="p-3">
                          <Badge variant="secondary" className={typeBadge.className}>
                            {typeBadge.label}
                          </Badge>
                        </td>
                        <td className="p-3 text-gray-600 whitespace-nowrap">{entry.date}</td>
                        <td className="p-3 font-medium">{entry.restaurant_name}</td>
                        <td className="p-3 text-right font-semibold whitespace-nowrap">
                          <CurrencyDisplay amount={entry.amount} />
                        </td>
                        <td className="p-3 text-gray-600">{entry.submitted_by}</td>
                        <td className="p-3 text-gray-500 max-w-48 truncate" title={entry.description}>
                          {entry.description}
                        </td>
                        {activeTab !== "pending" && (
                          <td className="p-3 text-xs text-gray-500">
                            {entry.reviewed_by && (
                              <div>
                                <div className="font-medium">{entry.reviewed_by}</div>
                                <div>{entry.reviewed_at?.slice(0, 10)}</div>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 w-7 p-0"
                              title="View Details"
                              onClick={() => { setDetailEntry(entry); setDetailDialog(true); setEditMode(false); }}
                              data-testid={`view-${entry.entry_id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            {activeTab === "pending" && (
                              <>
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-7 w-7 p-0 text-green-600 hover:text-green-700"
                                  title="Mark Reviewed"
                                  onClick={() => handleReview(entry.entry_type, entry.entry_id, "reviewed")}
                                  data-testid={`approve-${entry.entry_id}`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  title="Flag"
                                  onClick={() => { setFlagEntry(entry); setFlagNotes(""); setFlagDialog(true); }}
                                  data-testid={`flag-${entry.entry_id}`}
                                >
                                  <Flag className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            {activeTab === "flagged" && (
                              <Button
                                variant="ghost" size="sm"
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700"
                                title="Edit & Resolve"
                                onClick={() => {
                                  setDetailEntry(entry);
                                  setEditMode(true);
                                  setEditFields(entry.entry_type === "expense"
                                    ? { amount: entry.amount, description: entry.description?.split(" — ")[1] || "" }
                                    : entry.entry_type === "handover"
                                    ? { cash_handed_over: entry.amount, remarks: entry.description }
                                    : { notes: entry.description }
                                  );
                                  setDetailDialog(true);
                                }}
                                data-testid={`edit-${entry.entry_id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {activeTab === "reviewed" && (
                              <Button
                                variant="ghost" size="sm"
                                className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700"
                                title="Rollback to Pending"
                                onClick={() => handleRollback(entry.entry_type, entry.entry_id)}
                                data-testid={`rollback-${entry.entry_id}`}
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1" data-testid="review-pagination">
          <span className="text-xs text-gray-500">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              data-testid="review-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium">Page {page} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              data-testid="review-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Flag Dialog */}
      <Dialog open={flagDialog} onOpenChange={setFlagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Flag className="w-5 h-5" /> Flag Entry
            </DialogTitle>
          </DialogHeader>
          {flagEntry && (
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Type:</span> <Badge variant="secondary" className={TYPE_BADGES[flagEntry.entry_type]?.className}>{TYPE_BADGES[flagEntry.entry_type]?.label}</Badge></div>
                <div className="flex justify-between"><span className="text-gray-500">Restaurant:</span> <span className="font-medium">{flagEntry.restaurant_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Amount:</span> <span className="font-semibold"><CurrencyDisplay amount={flagEntry.amount} /></span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date:</span> <span>{flagEntry.date}</span></div>
              </div>
              <div className="space-y-2">
                <Label>Reason for flagging *</Label>
                <Textarea
                  value={flagNotes}
                  onChange={(e) => setFlagNotes(e.target.value)}
                  placeholder="Describe what's wrong with this entry..."
                  data-testid="flag-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleFlag} disabled={!flagNotes.trim()} data-testid="confirm-flag-btn">
              Flag Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / Edit Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editMode ? <><Pencil className="w-5 h-5 text-blue-600" /> Edit Flagged Entry</> : <><Eye className="w-5 h-5" /> Entry Details</>}
            </DialogTitle>
          </DialogHeader>
          {detailEntry && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Type:</span> <Badge variant="secondary" className={TYPE_BADGES[detailEntry.entry_type]?.className}>{TYPE_BADGES[detailEntry.entry_type]?.label}</Badge></div>
                <div><span className="text-gray-500">Date:</span> {detailEntry.date}</div>
                <div><span className="text-gray-500">Restaurant:</span> {detailEntry.restaurant_name}</div>
                <div><span className="text-gray-500">Submitted by:</span> {detailEntry.submitted_by}</div>
              </div>
              
              {!editMode ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 bg-gray-50 rounded"><span>Amount:</span> <span className="font-bold"><CurrencyDisplay amount={detailEntry.amount} /></span></div>
                  {/* Revenue category breakdown */}
                  {detailEntry.entry_type === "revenue" && detailEntry.amounts && Object.keys(detailEntry.amounts).length > 0 && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded space-y-1.5">
                      <div className="font-semibold text-xs text-green-700">Revenue Breakdown:</div>
                      {Object.entries(detailEntry.amounts).map(([catId, amt]) => {
                        const catName = categoryNames[catId] || catId;
                        const numAmt = Number(amt) || 0;
                        if (numAmt === 0) return null;
                        return (
                          <div key={catId} className="flex justify-between text-xs">
                            <span className="text-gray-600">{catName}</span>
                            <span className="font-medium"><CurrencyDisplay amount={numAmt} /></span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex justify-between p-2 bg-gray-50 rounded"><span>Description:</span> <span>{detailEntry.description}</span></div>
                  {detailEntry.detail && <div className="flex justify-between p-2 bg-gray-50 rounded"><span>Detail:</span> <span>{detailEntry.detail}</span></div>}
                  {detailEntry.review_notes && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700">
                      <div className="font-semibold text-xs">Review Notes:</div>
                      <div>{detailEntry.review_notes}</div>
                    </div>
                  )}
                  {detailEntry.reviewed_by && (
                    <div className="text-xs text-gray-400">
                      Reviewed by {detailEntry.reviewed_by} on {detailEntry.reviewed_at?.slice(0, 10)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {detailEntry.entry_type === "expense" && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs">Amount</Label>
                        <Input type="number" value={editFields.amount || ""} onChange={e => setEditFields({ ...editFields, amount: parseFloat(e.target.value) || 0 })} data-testid="edit-amount" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Description</Label>
                        <Input value={editFields.description || ""} onChange={e => setEditFields({ ...editFields, description: e.target.value })} data-testid="edit-description" />
                      </div>
                    </>
                  )}
                  {detailEntry.entry_type === "handover" && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs">Amount</Label>
                        <Input type="number" value={editFields.cash_handed_over || ""} onChange={e => setEditFields({ ...editFields, cash_handed_over: parseFloat(e.target.value) || 0 })} data-testid="edit-amount" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Remarks</Label>
                        <Textarea value={editFields.remarks || ""} onChange={e => setEditFields({ ...editFields, remarks: e.target.value })} />
                      </div>
                    </>
                  )}
                  {detailEntry.entry_type === "revenue" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Notes</Label>
                      <Input value={editFields.notes || ""} onChange={e => setEditFields({ ...editFields, notes: e.target.value })} data-testid="edit-notes" />
                    </div>
                  )}
                  {detailEntry.review_notes && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      <div className="font-semibold text-xs">Flag Reason:</div>
                      <div>{detailEntry.review_notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailDialog(false); setEditMode(false); }}>Close</Button>
            {editMode && (
              <Button onClick={handleEditSave} data-testid="save-edit-btn">Save & Mark Reviewed</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewAuditPage;
